// components/SegmentsFiltersSidebar.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, locationData, XIcon } from "./Icons";
import { SectionHeader } from "./SectionHeader";

const BurgerIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="3" y1="6" x2="21" y2="6"></line>
		<line x1="3" y1="12" x2="21" y2="12"></line>
		<line x1="3" y1="18" x2="21" y2="18"></line>
	</svg>
);

// Fixed MultiSelectDropdown Component with working checkbox clicks
const MultiSelectDropdown = ({
	label,
	options,
	value = [],
	onChange,
	placeholder = "Select options...",
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
	});
	const [pendingSelections, setPendingSelections] = useState(value);

	const dropdownRef = useRef(null);
	const triggerRef = useRef(null);

	// Update pendingSelections when value prop changes
	useEffect(() => {
		setPendingSelections(value);
	}, [value]);

	// Enhanced positioning function that accounts for scroll
	const updateDropdownPosition = useCallback(() => {
		if (triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
				width: rect.width,
			});
		}
	}, []);

	// Add scroll event listener to update position
	useEffect(() => {
		if (isOpen) {
			updateDropdownPosition();

			const handleScroll = () => {
				updateDropdownPosition();
			};

			window.addEventListener("scroll", handleScroll, true);
			document.addEventListener("scroll", handleScroll, true);
			window.addEventListener("resize", handleScroll);

			return () => {
				window.removeEventListener("scroll", handleScroll, true);
				document.removeEventListener("scroll", handleScroll, true);
				window.removeEventListener("resize", handleScroll);
			};
		}
	}, [isOpen, updateDropdownPosition]);

	// Click outside handler
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				triggerRef.current &&
				!triggerRef.current.contains(event.target)
			) {
				setIsOpen(false);
				setSearchTerm("");
				setPendingSelections(value);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [value]);

	const filteredOptions = options.filter((option) =>
		option.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	const toggleOption = (option) => {
		setPendingSelections((prev) => {
			if (prev.includes(option)) {
				return prev.filter((v) => v !== option);
			} else {
				return [...prev, option];
			}
		});
	};

	const handleDone = () => {
		onChange(pendingSelections);
		setIsOpen(false);
		setSearchTerm("");
	};

	const clearAll = () => {
		setPendingSelections([]);
	};

	const selectAll = () => {
		setPendingSelections([...options]);
	};

	const handleTriggerClick = () => {
		if (!isOpen) {
			setPendingSelections(value);
		}
		setIsOpen(!isOpen);
	};

	return (
		<div className="multiselect-container">
			<label className="multiselect-label">
				{label}
				{value.length > 0 && (
					<span className="selected-count">({value.length} selected)</span>
				)}
			</label>

			<div
				ref={triggerRef}
				className={`multiselect-trigger ${isOpen ? "open" : ""}`}
				onClick={handleTriggerClick}
			>
				<span className="multiselect-placeholder">
					{value.length === 0
						? placeholder
						: `${value.length} item${value.length !== 1 ? "s" : ""} selected`}
				</span>
				<ChevronDownIcon />
			</div>

			{isOpen &&
				createPortal(
					<div
						ref={dropdownRef}
						className="multiselect-dropdown-portal"
						style={{
							position: "absolute",
							top: dropdownPosition.top,
							left: dropdownPosition.left,
							width: dropdownPosition.width,
							zIndex: 10000,
							pointerEvents: "auto",
						}}
					>
						<div className="multiselect-header">
							<input
								type="text"
								placeholder="Search..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="multiselect-search"
								onClick={(e) => e.stopPropagation()}
							/>
							<div className="multiselect-actions">
								<button
									type="button"
									onClick={selectAll}
									className="select-all-btn"
								>
									Select All
								</button>
								<button
									type="button"
									onClick={clearAll}
									className="clear-all-btn"
								>
									Clear
								</button>
							</div>
						</div>

						<div className="multiselect-options">
							{filteredOptions.map((option) => (
								<div
									key={option}
									className={`multiselect-option ${pendingSelections.includes(option) ? "selected" : ""}`}
									onClick={() => toggleOption(option)}
								>
									{/* FIXED: Remove stopPropagation and make checkbox functional */}
									<input
										type="checkbox"
										checked={pendingSelections.includes(option)}
										onChange={() => toggleOption(option)}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									/>
									<span className="option-text">{option}</span>
								</div>
							))}
							{filteredOptions.length === 0 && (
								<div className="no-options">No options found</div>
							)}
						</div>

						<div
							style={{
								padding: "8px",
								borderTop: "1px solid #e5e7eb",
								textAlign: "right",
								backgroundColor: "#f8f9fa",
							}}
						>
							<div
								style={{
									fontSize: "9px",
									color: "#6b7280",
									marginBottom: "6px",
									textAlign: "center",
									fontStyle: "italic",
								}}
							>
								Press "Done" to save selections
							</div>

							<button
								type="button"
								onClick={handleDone}
								style={{
									background: "#0076BD",
									color: "white",
									border: "none",
									borderRadius: "4px",
									padding: "6px 12px",
									fontSize: "11px",
									cursor: "pointer",
									fontWeight: "500",
								}}
							>
								Done
							</button>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};

// Tag-based Filter Component for smaller lists
const TagFilter = ({ label, options, value = [], onChange }) => {
	const toggleOption = (option) => {
		const isSelected = value.includes(option);
		const newValue = isSelected
			? value.filter((v) => v !== option)
			: [...value, option];
		onChange(newValue);
	};

	return (
		<div className="tag-filter-container">
			<label className="tag-filter-label">
				{label}
				{value.length > 0 && (
					<span className="selected-count">({value.length} selected)</span>
				)}
			</label>
			<div className="tag-filter-options">
				{options.map((option) => {
					const isSelected = value.includes(option);
					return (
						<span
							key={option}
							className={`tag tag-clickable ${isSelected ? "tag-selected" : "tag-gray"}`}
							onClick={() => toggleOption(option)}
						>
							{option}
						</span>
					);
				})}
			</div>
		</div>
	);
};

// Boolean Filter Component
const BooleanFilter = ({
	label,
	value,
	onChange,
	trueLabel = "Yes",
	falseLabel = "No",
}) => {
	return (
		<div className="boolean-filter-container">
			<label className="boolean-filter-label">{label}</label>
			<div className="boolean-filter-options">
				<div className="tab-selector">
					<button
						type="button"
						onClick={() => onChange(null)}
						className={`tab-button ${value === null ? "active" : ""}`}
					>
						All
					</button>
					<button
						type="button"
						onClick={() => onChange(true)}
						className={`tab-button ${value === true ? "active" : ""}`}
					>
						{trueLabel}
					</button>
					<button
						type="button"
						onClick={() => onChange(false)}
						className={`tab-button ${value === false ? "active" : ""}`}
					>
						{falseLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

// Age Range Filter Component
const AgeRangeFilter = ({ label, value = {}, onChange }) => {
	const handleLowerChange = (e) => {
		const start = parseInt(e.target.value) || "";
		onChange({ ...value, start });
	};

	const handleUpperChange = (e) => {
		const end = parseInt(e.target.value) || "";
		onChange({ ...value, end });
	};

	return (
		<div className="age-range-container">
			<label className="age-range-label">{label}</label>
			<div className="age-range-inputs">
				<div className="age-input-group">
					<label className="age-input-label">Lower Limit</label>
					<input
						type="number"
						value={value.start || ""}
						onChange={handleLowerChange}
						placeholder="Min age"
						className="age-input"
						min="0"
						max="120"
					/>
				</div>
				<div className="age-input-group">
					<label className="age-input-label">Upper Limit</label>
					<input
						type="number"
						value={value.end || ""}
						onChange={handleUpperChange}
						placeholder="Max age"
						className="age-input"
						min="0"
						max="120"
					/>
				</div>
			</div>
		</div>
	);
};

// Gender Filter Component
const GenderFilter = ({ label, value, onChange }) => {
	return (
		<div className="gender-filter-container">
			<label className="gender-filter-label">{label}</label>
			<div className="location-type-selector">
				<div className="tab-selector">
					<button
						type="button"
						onClick={() => onChange(null)}
						className={`tab-button ${value === null ? "active" : ""}`}
					>
						All
					</button>
					<button
						type="button"
						onClick={() => onChange("Male")}
						className={`tab-button ${value === "Male" ? "active" : ""}`}
					>
						Male
					</button>
					<button
						type="button"
						onClick={() => onChange("Female")}
						className={`tab-button ${value === "Female" ? "active" : ""}`}
					>
						Female
					</button>
				</div>
			</div>
		</div>
	);
};

// Updated Location Filter Component
const LocationFilter = ({ label, value = {}, onChange }) => {
	// Use imported location data
	const {
		states: allStates,
		dmas: allDmas,
		congressional: allCongressional,
	} = locationData;

	// Current value structure: { type: 'state'|'dma'|'congressional', selections: [] }
	const currentValue =
		value && typeof value === "object"
			? value
			: {
					type: null,
					selections: [],
				};

	// Store previous type to detect when it changes
	const [previousType, setPreviousType] = React.useState(currentValue.type);

	// Detect if type changed externally (from LLM) vs user click
	React.useEffect(() => {
		if (currentValue.type !== previousType && currentValue.type !== null) {
			console.log("🔄 Location type changed externally:", {
				from: previousType,
				to: currentValue.type,
				selections: currentValue.selections,
			});
			setPreviousType(currentValue.type);
		}
	}, [currentValue.type, previousType]);

	const handleTypeChange = (newType) => {
		console.log("👆 User clicked location type tab:", newType);
		// Only clear selections when user manually switches tabs
		// This prevents clearing when LLM sets the type
		if (currentValue.type === newType) {
			// Same type clicked, do nothing
			return;
		}

		console.log("🗑️ Clearing selections due to manual tab switch");
		setPreviousType(newType);
		onChange({ type: newType, selections: [] });
	};

	const handleSelectionsChange = (newSelections) => {
		onChange({ ...currentValue, selections: newSelections });
	};

	const getOptionsForType = () => {
		switch (currentValue.type) {
			case "dma":
				return allDmas;
			case "congressional":
				return allCongressional;
			default:
				return allStates;
		}
	};

	const getPlaceholderForType = () => {
		switch (currentValue.type) {
			case "dma":
				return "Select DMAs...";
			case "congressional":
				return "Select Congressional Districts...";
			default:
				return "Select States...";
		}
	};

	return (
		<div className="location-filter-container">
			<label className="location-filter-label">
				{label}
				{currentValue.selections?.length > 0 && (
					<span className="selected-count">
						({currentValue.selections.length} selected)
					</span>
				)}
			</label>

			{/* Type Selector */}
			<div className="location-type-selector">
				<div className="tab-selector">
					<button
						type="button"
						onClick={() => handleTypeChange("state")}
						className={`tab-button ${currentValue.type === "state" ? "active" : ""}`}
					>
						State
					</button>
					<button
						type="button"
						onClick={() => handleTypeChange("dma")}
						className={`tab-button ${currentValue.type === "dma" ? "active" : ""}`}
					>
						DMA
					</button>
					<button
						type="button"
						onClick={() => handleTypeChange("congressional")}
						className={`tab-button ${currentValue.type === "congressional" ? "active" : ""}`}
					>
						Congressional
					</button>
				</div>
			</div>

			{/* MultiSelect Dropdown */}
			<MultiSelectDropdown
				label={`Selected ${currentValue.type === "state" ? "States" : currentValue.type === "dma" ? "DMAs" : "Congressional Districts"}`}
				options={getOptionsForType()}
				value={currentValue.selections || []}
				onChange={handleSelectionsChange}
				placeholder={getPlaceholderForType()}
			/>

			{/* Display current selections */}
			{currentValue.selections?.length > 0 && (
				<div className="location-selection-summary">
					<div className="selection-tags">
						{currentValue.selections.slice(0, 3).map((selection, index) => (
							<span key={index} className="selection-tag">
								{selection}
							</span>
						))}
						{currentValue.selections.length > 3 && (
							<span className="selection-tag-more">
								+{currentValue.selections.length - 3} more
							</span>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

const ThresholdInput = React.memo(
	({ field, label, hint, placeholder, value, defaultValue, onChange }) => {
		const [localValue, setLocalValue] = useState("");
		const [isFocused, setIsFocused] = useState(false);

		// Helper function to format large numbers for display
		const formatNumber = (num) => {
			if (num >= 1000000000000) {
				return (num / 1000000000000).toFixed(1) + "T";
			} else if (num >= 1000000000) {
				return (num / 1000000000).toFixed(1) + "B";
			} else if (num >= 1000000) {
				return (num / 1000000).toFixed(1) + "M";
			} else if (num >= 1000) {
				return (num / 1000).toFixed(1) + "K";
			}
			return num.toString();
		};

		// Helper function to parse input values
		const parseInputValue = (inputStr) => {
			if (!inputStr || inputStr === "") {
				return defaultValue;
			}

			const str = inputStr.toString().toLowerCase();
			let multiplier = 1;
			let baseValue = str;

			if (str.includes("t")) {
				multiplier = 1000000000000;
				baseValue = str.replace("t", "");
			} else if (str.includes("b")) {
				multiplier = 1000000000;
				baseValue = str.replace("b", "");
			} else if (str.includes("m")) {
				multiplier = 1000000;
				baseValue = str.replace("m", "");
			} else if (str.includes("k")) {
				multiplier = 1000;
				baseValue = str.replace("k", "");
			}

			const parsed = parseFloat(baseValue);
			return isNaN(parsed) ? defaultValue : parsed * multiplier;
		};

		// Update local value when prop value changes (but not when focused)
		useEffect(() => {
			if (!isFocused) {
				setLocalValue(formatNumber(value || defaultValue));
			}
		}, [value, defaultValue, isFocused]);

		const handleChange = useCallback((e) => {
			setLocalValue(e.target.value);
		}, []);

		const handleFocus = useCallback(
			(e) => {
				setIsFocused(true);
				// Show raw number when focused
				setLocalValue((value || defaultValue).toString());
			},
			[value, defaultValue],
		);

		const handleBlur = useCallback(() => {
			setIsFocused(false);
			const parsedValue = parseInputValue(localValue);

			// Update parent if value changed
			if (parsedValue !== value) {
				onChange(field, parsedValue);
			}

			// Format the display value
			setLocalValue(formatNumber(parsedValue));
		}, [localValue, value, field, onChange]);

		const handleKeyPress = useCallback((e) => {
			if (e.key === "Enter") {
				e.target.blur(); // This will trigger handleBlur
			}
		}, []);

		return (
			<div className="threshold-input-group">
				<label className="threshold-input-label">
					{label}
					<span className="threshold-hint">({hint})</span>
				</label>
				<div className="threshold-input-wrapper">
					<input
						type="text"
						placeholder={placeholder}
						className="threshold-input"
						value={localValue}
						onChange={handleChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onKeyPress={handleKeyPress}
					/>
					<span className="threshold-display">
						Current: {formatNumber(value || defaultValue)}
					</span>
				</div>
			</div>
		);
	},
);

const ThresholdFilter = ({ label, value = {}, onChange }) => {
	// Default threshold values as specified
	const defaultThresholds = {
		reach_relevance: 38000000, // 10,000 billion (10 trillion)
		uniqueUsers: 1500000000,
		weightedImpressions: 700000,
		reach_composite: 38000000,
	};

	const handleThresholdChange = useCallback(
		(field, newValue) => {
			onChange({
				...value,
				[field]: newValue,
			});
		},
		[value, onChange],
	);

	const currentValues = {
		reach_relevance: value.reach_relevance || defaultThresholds.reach_relevance,
		uniqueUsers: value.uniqueUsers || defaultThresholds.uniqueUsers,
		weightedImpressions:
			value.weightedImpressions || defaultThresholds.weightedImpressions,
		reach_composite: value.reach_composite || defaultThresholds.reach_composite,
	};

	return (
		<div className="threshold-filter-container">
			<label className="threshold-filter-label">{label}</label>
			<div className="threshold-filter-content">
				<ThresholdInput
					field="reach_relevance"
					label="Relevance(Reach) Threshold"
					hint="Default: 38M"
					placeholder="38000000"
					value={currentValues.reach_relevance}
					defaultValue={defaultThresholds.reach_relevance}
					onChange={handleThresholdChange}
				/>

				<ThresholdInput
					field="uniqueUsers"
					label="Unique Users Threshold"
					hint="Default: 1.5B"
					placeholder="1500000000"
					value={currentValues.uniqueUsers}
					defaultValue={defaultThresholds.uniqueUsers}
					onChange={handleThresholdChange}
				/>

				<ThresholdInput
					field="weightedImpressions"
					label="Impressions Threshold"
					hint="Default: 700K"
					placeholder="700000"
					value={currentValues.weightedImpressions}
					defaultValue={defaultThresholds.weightedImpressions}
					onChange={handleThresholdChange}
				/>

				<ThresholdInput
					field="reach_composite"
					label="Composite Score(Reach) Threshold"
					hint="Default: 38M"
					placeholder="38000000"
					value={currentValues.reach_composite}
					defaultValue={defaultThresholds.reach_composite}
					onChange={handleThresholdChange}
				/>

				<div className="threshold-help">
					<small>
						💡 You can use suffixes: K (thousands), M (millions), B (billions),
						T (trillions)
					</small>
				</div>
			</div>
		</div>
	);
};

// Category Filter Input Component
const CategoryFilterInput = React.memo(
	({ value, onChange, placeholder, field }) => {
		const [localValue, setLocalValue] = useState(value || "");

		useEffect(() => {
			setLocalValue(value || "");
		}, [value]);

		const handleChange = useCallback((e) => {
			setLocalValue(e.target.value);
		}, []);

		const handleBlur = useCallback(() => {
			const numValue = localValue === "" ? null : parseFloat(localValue);
			const finalValue = isNaN(numValue) ? null : numValue;
			if (finalValue !== value) {
				onChange(finalValue);
			}
		}, [localValue, value, onChange]);

		const handleKeyPress = useCallback(
			(e) => {
				if (e.key === "Enter") {
					handleBlur();
				}
			},
			[handleBlur],
		);

		return (
			<input
				type="number"
				value={localValue}
				onChange={handleChange}
				onBlur={handleBlur}
				onKeyPress={handleKeyPress}
				placeholder={placeholder}
				className="category-filter-input"
			/>
		);
	},
);

// Category Set Filter Component
const CategorySetFilter = ({ label, value = [], onChange }) => {
	// Available categoryL2 options with their corresponding attribute names
	const categoryOptions = [
		{ categoryL2: "Auto Intenders", attributeName: "Vehicle Price Range" },
		{ categoryL2: "Auto Ownership", attributeName: "Vehicle Age" },
		{ categoryL2: "Home Value", attributeName: null },
		{ categoryL2: "Household Income", attributeName: null },
	];

	const addCategoryFilter = () => {
		if (value.length < 4) {
			const newFilter = {
				categoryL2: "",
				attributeName: null,
				numericMin: null,
				numericMax: null,
			};
			onChange([...value, newFilter]);
		}
	};

	const removeCategoryFilter = (index) => {
		const newFilters = value.filter((_, i) => i !== index);
		onChange(newFilters);
	};

	const updateCategoryFilter = (index, field, newValue) => {
		const newFilters = [...value];
		newFilters[index] = { ...newFilters[index], [field]: newValue };

		// If categoryL2 changes, update attributeName accordingly
		if (field === "categoryL2") {
			const selectedOption = categoryOptions.find(
				(opt) => opt.categoryL2 === newValue,
			);
			newFilters[index].attributeName = selectedOption
				? selectedOption.attributeName
				: null;
		}

		onChange(newFilters);
	};

	const getAvailableOptions = (currentIndex) => {
		const usedCategories = value
			.map((filter, index) =>
				index !== currentIndex ? filter.categoryL2 : null,
			)
			.filter(Boolean);

		return categoryOptions.filter(
			(option) => !usedCategories.includes(option.categoryL2),
		);
	};

	return (
		<div className="category-set-filter-container">
			<div className="category-set-header">
				<label className="category-set-label">
					{label}
					{value.length > 0 && (
						<span className="selected-count">({value.length} filters)</span>
					)}
				</label>

				{value.length < 4 && (
					<button
						type="button"
						onClick={addCategoryFilter}
						className="add-category-btn"
					>
						+ Add Filter
					</button>
				)}
			</div>

			<div className="category-filters-list">
				{value.map((filter, index) => (
					<div key={index} className="category-filter-item">
						<div className="category-filter-header">
							<span className="filter-number">Filter {index + 1}</span>
							<button
								type="button"
								onClick={() => removeCategoryFilter(index)}
								className="remove-filter-btn"
							>
								<XIcon />
							</button>
						</div>

						<div className="category-filter-content">
							{/* Category L2 Dropdown */}
							<div className="filter-field">
								<label className="filter-field-label">Category</label>
								<select
									value={filter.categoryL2}
									onChange={(e) =>
										updateCategoryFilter(index, "categoryL2", e.target.value)
									}
									className="category-select"
								>
									<option value="">Select category...</option>
									{getAvailableOptions(index).map((option) => (
										<option key={option.categoryL2} value={option.categoryL2}>
											{option.categoryL2}
										</option>
									))}
								</select>
							</div>

							{/* Min/Max Value Inputs */}
							{filter.categoryL2 && (
								<div className="min-max-inputs">
									<div className="filter-field">
										<label className="filter-field-label">Min Value</label>
										<CategoryFilterInput
											value={filter.numericMin}
											onChange={(value) =>
												updateCategoryFilter(index, "numericMin", value)
											}
											placeholder="Min"
											field="numericMin"
										/>
									</div>
									{/* <div className="filter-field">
                                        <label className="filter-field-label">Max Value</label>
                                        <CategoryFilterInput
                                            value={filter.numericMax}
                                            onChange={(value) => updateCategoryFilter(index, 'numericMax', value)}
                                            placeholder="Max"
                                            field="numericMax"
                                        />
                                    </div> */}
								</div>
							)}

							{/* Show attribute name if available */}
							{filter.categoryL2 && filter.attributeName && (
								<div className="attribute-display">
									<span className="attribute-label">
										Attribute: {filter.attributeName}
									</span>
								</div>
							)}
						</div>
					</div>
				))}

				{value.length === 0 && (
					<div className="no-filters-message">
						No category filters added. Click "Add Filter" to create one.
					</div>
				)}
			</div>
		</div>
	);
};

export const SegmentsFiltersSidebar = ({
	showFilters,
	filters,
	expandedSections,
	onToggleSection,
	onUpdateFilter,
	onToggleArrayFilter,
	onClearAllFilters,
	onClose,
	filterOptions,
	isLoadingFilterOptions,
	hasFilterError,
	filterError,
	retryFetchFilterOptions,
	fetchFilterOptions,
	validation,
	hasActiveFilters,
	activeFilterCount,
	filterSummary,
	activeSubTab,
	onSubTabChange,
}) => {
	// ✅ UPDATED: Merge API filter options with fallback values
	const getFilterValues = useCallback(() => {
		// Static fallback filter values
		const fallbackValues = {
			accountName: [
				"Adstra",
				"Affinity Answers Org",
				"AnalyticsIQ Inc",
				"Bombora",
				"Choreograph",
				"Dun & Bradstreet",
				"Epsilon",
				"Experian Org",
				"Merkle",
				"National Cinemedia",
				"OpenAudience",
				"Oracle",
				"Reklaim",
				"Sports Innovation Lab",
				"Zeta Global Org",
			],
			status: ["error", "expired", "ready"],
			type: ["1p", "3p", "demographic", "geo", "iab_site_categories", "rtg"],
			subType: [
				"",
				"9D",
				"adstra",
				"AFF",
				"affinityanswers",
				"age",
				"analyticsiq",
				"bombora",
				"choreograph",
				"congressional",
				"dma",
				"dun_bradstreet",
				"epsilon",
				"EXP",
				"experiandirect",
				"gender",
				"merkle",
				"neustar",
				"oracle",
				"reklaim",
				"sportslab",
				"state",
				"zetaglobal",
			],
			allowedCountries: ['["us"]', '["US"]'],
			category: [
				"demographic_age",
				"demographic_gender",
				"direct",
				"location",
				"predefined",
			],
			subCategory: [
				"bulk",
				"congressional",
				"dma",
				"partner_audiences",
				"past_browsing_interests",
				"state",
				"third_party_data",
			],
			source: [
				"affinity",
				"experian",
				"greatdata",
				"neustar",
				"ninthdecimal",
				"openx",
			],
			providerId: [
				"adstra",
				"affinityanswers",
				"analyticsiq",
				"bombora",
				"choreograph",
				"dun_bradstreet",
				"epsilon",
				"experiandirect",
				"merkle",
				"neustar",
				"openaudience",
				"oracle",
				"reklaim",
				"sportslab",
				"zetaglobal",
			],
			categoryL1: [
				"Activities & Hobbies",
				"Auto Intenders",
				"Auto Ownership",
				"B2B",
				"Behavioral",
				"Consumer Expenditures",
				"Demographic",
				"Financial",
				"Food & Beverage",
				"Geographic",
				"Health & Fitness",
				"In-Market (General)",
				"Intent",
				"Interest",
				"Life Events",
				"Lifestage",
				"Location Visits",
				"Media Consumption",
				"Occupation",
				"Online Activity",
				"Political",
				"Political Affiliation",
				"Property Info",
				"Psychographic",
				"Purchase Behavior",
				"Real Estate",
				"Religion",
				"Technographic",
				"Travel",
			],
			categoryL2: [
				"Activities & Hobbies",
				"Area Type",
				"Auto Intenders",
				"Auto Ownership",
				"Charitable Donations",
				"Congressional District",
				"Consumer Expenditures",
				"Credit Card Usage",
				"DMA",
				"Education",
				"Ethnicity",
				"Food & Beverage",
				"Gambling",
				"Health & Fitness",
				"Home Intenders",
				"Home Value",
				"Household Composition",
				"Household Income",
				"Industry",
				"In-Market (General)",
				"Insurance",
				"Investments",
				"Job Function",
				"Language",
				"Life Events",
				"Lifestage",
				"Location Visits",
				"Marital Status",
				"Media Consumption",
				"Media Viewership",
				"Military Status",
				"Mortgages & Loans",
				"Music",
				"Occupation",
				"Online Activity",
				"Person Type",
				"Pets",
				"Political Affiliation",
				"Property Info",
				"Purchase Behavior",
				"Religion",
				"Seniority",
				"Software Usage",
				"Sports",
				"State",
				"Technology Installed",
				"Travel",
			],
			audienceType: ["B2B", "Consumer"],
			valueQualifier: [
				"",
				"Approximately",
				"Current",
				"Declared",
				"During March",
				"Extremely likely",
				"Extremely Likely",
				"Extremely unlikely",
				"Frequently",
				"High",
				"High Likelihood",
				"Highly",
				"Highly likely",
				"Highly Likely",
				"Highly unlikely",
				"Household Members",
				"Index",
				"In-market",
				"Interest",
				"likelihood",
				"Likelihood",
				"Likelihood to be low",
				"Likely",
				"Local",
				"Low",
				"low likelihood",
				"Low likelihood",
				"Low Likelihood",
				"Low Likely",
				"Loyalists",
				"Medium",
				"medium likelihood",
				"Medium Likelihood",
				"Not Likely",
				"Not to have",
				"Percentile",
				"Predicted",
				"Propensity",
				"Recently",
				"Recently Seen",
				"Recently Visited",
				"Recently Visiting",
				"Seen",
				"Somewhat",
				"Somewhat likely",
				"Somewhat Likely",
				"Somewhat unlikely",
				"Unlikely",
				"Users",
				"Very likely",
				"Very unlikely",
				"Visitors",
				"Visits",
			],
		};

		// ✅ If we have API filter options, merge them with fallbacks
		if (filterOptions && typeof filterOptions === "object") {
			console.log("🔧 Using API filter options:", Object.keys(filterOptions));

			return {
				// Map API response fields to expected field names
				accountName: filterOptions.accountName || fallbackValues.accountName,
				status: filterOptions.status || fallbackValues.status,
				type: filterOptions.type || fallbackValues.type,
				subType: filterOptions.subType || fallbackValues.subType,
				allowedCountries:
					filterOptions.allowedCountries || fallbackValues.allowedCountries,
				category: filterOptions.category || fallbackValues.category,
				subCategory: filterOptions.subCategory || fallbackValues.subCategory,
				source: filterOptions.source || fallbackValues.source,
				providerId: filterOptions.providerId || fallbackValues.providerId,
				categoryL1: filterOptions.categoryL1 || fallbackValues.categoryL1,
				categoryL2: filterOptions.categoryL2 || fallbackValues.categoryL2,
				audienceType: filterOptions.audienceType || fallbackValues.audienceType,
				valueQualifier:
					filterOptions.valueQualifier || fallbackValues.valueQualifier,
			};
		}

		console.log("⚠️ Using fallback filter values (API not available)");
		return fallbackValues;
	}, [filterOptions]);

	// ✅ NEW: Get the current filter values (API or fallback)
	const segmentFilterValues = getFilterValues();

	// Track if we've already initialized the default filters
	const hasInitializedDefaults = useRef(false);

	// ✅ NEW: Initialize accountName and providerId with ALL options by default
	useEffect(() => {
		// Only initialize once and only if we have the filter values available
		if (
			!hasInitializedDefaults.current &&
			segmentFilterValues.accountName &&
			segmentFilterValues.providerId
		) {
			const needsAccountNameInit =
				!filters.accountName || filters.accountName.length === 0;
			const needsProviderIdInit =
				!filters.providerId || filters.providerId.length === 0;

			if (needsAccountNameInit || needsProviderIdInit) {
				console.log("🎯 Initializing default filters (ALL SELECTED):");

				if (needsAccountNameInit) {
					console.log(
						"  - accountName:",
						segmentFilterValues.accountName.length,
						"options",
					);
					onUpdateFilter("accountName", [...segmentFilterValues.accountName]);
				}

				if (needsProviderIdInit) {
					console.log(
						"  - providerId:",
						segmentFilterValues.providerId.length,
						"options",
					);
					onUpdateFilter("providerId", [...segmentFilterValues.providerId]);
				}

				hasInitializedDefaults.current = true;
			}
		}
	}, [
		segmentFilterValues.accountName,
		segmentFilterValues.providerId,
		filters.accountName,
		filters.providerId,
		onUpdateFilter,
	]);

	// ✅ NEW: Loading indicator component
	const LoadingIndicator = () => (
		<div className="filter-loading">
			<div className="loading-spinner"></div>
			<span>Loading filter options...</span>
		</div>
	);

	// ✅ NEW: Retry button component
	const RetryButton = () => (
		<div className="filter-retry">
			<button onClick={retryFetchFilterOptions} className="retry-button">
				Retry Loading Filters
			</button>
			{fetchFilterOptions && (
				<button
					onClick={() => fetchFilterOptions(true)}
					className="refresh-button"
				>
					↻ Refresh Filters
				</button>
			)}
		</div>
	);

	const [expandedCategories, setExpandedCategories] = useState({
		basic: false,
		categories: false,
		dataSource: false,
		qualifiers: false,
		boolean: false,
		thresholds: false,
		categorySet: false,
		scale: false,
	});

	const handleToggleCategory = useCallback((categoryName) => {
		setExpandedCategories((prev) => ({
			...prev,
			[categoryName]: !prev[categoryName],
		}));
	}, []);

	if (!showFilters) return null;

	const CategoryHeader = ({ title, isExpanded, onClick, children }) => (
		<div className="filter-category-box">
			<div
				className={`category-header ${isExpanded ? "expanded" : "collapsed"}`}
				onClick={onClick}
			>
				<h3 className="category-title-new">{title}</h3>
				<div
					className={`category-chevron ${isExpanded ? "expanded" : "collapsed"}`}
				>
					<ChevronDownIcon />
				</div>
			</div>
			{isExpanded && <div className="category-content">{children}</div>}
		</div>
	);

	return (
		<>
			<div className="filters-sidebar">
				<div className="filters-header">
					<h2 className="filters-title">Segment Filters</h2>
					<div className="filters-actions">
						<button className="btn-medium-primary" onClick={onClearAllFilters}>
							Clear All
						</button>
						<button onClick={onClose} className="close-btn">
							<BurgerIcon />
						</button>
					</div>
				</div>

				<div className="filters-content">
					{/* Demographics Section - Always visible at top */}
					<div className="demographics-always-visible">
						<div className="demographics-header">
							<h3 className="demographics-title">Demographics</h3>
						</div>
						<div className="demographics-content">
							<AgeRangeFilter
								label="Demographic Age"
								value={filters.demographicAge || {}}
								onChange={(value) => onUpdateFilter("demographicAge", value)}
							/>

							<GenderFilter
								label="Demographic Gender"
								value={filters.demographicGender}
								onChange={(value) => onUpdateFilter("demographicGender", value)}
							/>

							<LocationFilter
								label="Demographic Location"
								value={filters.demographicLocation || {}}
								onChange={(value) =>
									onUpdateFilter("demographicLocation", value)
								}
							/>
						</div>
					</div>

					{/* Data Source */}
					<CategoryHeader
						title="Source and Providers"
						isExpanded={expandedCategories.dataSource}
						onClick={() => handleToggleCategory("dataSource")}
					>
						<div className="filter-section-spacing">
							<MultiSelectDropdown
								label="Account Name"
								options={segmentFilterValues.accountName}
								value={filters.accountName || []}
								onChange={(value) => onUpdateFilter("accountName", value)}
								placeholder="Select accounts..."
							/>

							<MultiSelectDropdown
								label="Provider ID"
								options={segmentFilterValues.providerId}
								value={filters.providerId || []}
								onChange={(value) => onUpdateFilter("providerId", value)}
								placeholder="Select providers..."
							/>

							<TagFilter
								label="Source"
								options={segmentFilterValues.source}
								value={filters.source || []}
								onChange={(value) => onUpdateFilter("source", value)}
							/>

							<TagFilter
								label="Status"
								options={segmentFilterValues.status}
								value={filters.status || []}
								onChange={(value) => onUpdateFilter("status", value)}
							/>

							<TagFilter
								label="Type"
								options={segmentFilterValues.type}
								value={filters.type || []}
								onChange={(value) => onUpdateFilter("type", value)}
							/>

							<TagFilter
								label="Audience Type"
								options={segmentFilterValues.audienceType}
								value={filters.audienceType || []}
								onChange={(value) => onUpdateFilter("audienceType", value)}
							/>

							<TagFilter
								label="Allowed Countries"
								options={segmentFilterValues.allowedCountries}
								value={filters.allowedCountries || []}
								onChange={(value) => onUpdateFilter("allowedCountries", value)}
							/>
						</div>
					</CategoryHeader>

					{/* Category Filters */}
					<CategoryHeader
						title="Categories and Value qualifier"
						isExpanded={expandedCategories.categories}
						onClick={() => handleToggleCategory("categories")}
					>
						<div className="filter-section-spacing">
							<MultiSelectDropdown
								label="Category L1"
								options={segmentFilterValues.categoryL1}
								value={filters.categoryL1 || []}
								onChange={(value) => onUpdateFilter("categoryL1", value)}
								placeholder="Select L1 categories..."
							/>

							<MultiSelectDropdown
								label="Category L2"
								options={segmentFilterValues.categoryL2}
								value={filters.categoryL2 || []}
								onChange={(value) => onUpdateFilter("categoryL2", value)}
								placeholder="Select L2 categories..."
							/>

							<MultiSelectDropdown
								label="Sub Type"
								options={segmentFilterValues.subType.filter(
									(item) => item !== "",
								)}
								value={filters.subType || []}
								onChange={(value) => onUpdateFilter("subType", value)}
								placeholder="Select sub types..."
							/>

							<MultiSelectDropdown
								label="Value Qualifier"
								options={segmentFilterValues.valueQualifier.filter(
									(item) => item !== "",
								)}
								value={filters.valueQualifier || []}
								onChange={(value) => onUpdateFilter("valueQualifier", value)}
								placeholder="Select qualifiers..."
							/>
						</div>
					</CategoryHeader>

					{/* Boolean Filters */}
					<CategoryHeader
						title="Segments Attribute"
						isExpanded={expandedCategories.boolean}
						onClick={() => handleToggleCategory("boolean")}
					>
						<div className="filter-section-spacing">
							<BooleanFilter
								label="Sellable"
								value={filters.sellable}
								onChange={(value) => onUpdateFilter("sellable", value)}
							/>
						</div>
					</CategoryHeader>

					<CategoryHeader
						title="Set Threshold"
						isExpanded={expandedCategories.thresholds}
						onClick={() => handleToggleCategory("thresholds")}
					>
						<div className="filter-section-spacing">
							<ThresholdFilter
								label="Cumulative Sets Thresholds"
								value={filters.cumulativeThresholds || {}}
								onChange={(value) =>
									onUpdateFilter("cumulativeThresholds", value)
								}
							/>
						</div>
					</CategoryHeader>

					<CategoryHeader
						title="Category Set"
						isExpanded={expandedCategories.categorySet}
						onClick={() => handleToggleCategory("categorySet")}
					>
						<div className="filter-section-spacing">
							<CategorySetFilter
								label="Category Filters"
								value={filters.categoryFilters || []}
								onChange={(value) => onUpdateFilter("categoryFilters", value)}
							/>
						</div>
					</CategoryHeader>

					<CategoryHeader
						title="Scale and Performance"
						isExpanded={expandedCategories.scale}
						onClick={() => handleToggleCategory("scale")}
					>
						<div className="section-content" style={{ padding: "12px 0" }}>
							<ThresholdInput
								field="reach"
								label="Reach"
								hint="0"
								placeholder="0"
								value={filters.reach}
								defaultValue="0"
								onChange={(field, value) => onUpdateFilter("reach", value)}
							/>

							<ThresholdInput
								field="weightedImpressions"
								label="Impressions"
								hint="0"
								placeholder="0"
								value={filters.weightedImpressions}
								defaultValue="0"
								onChange={(field, value) =>
									onUpdateFilter("weightedImpressions", value)
								}
							/>

							<ThresholdInput
								field="weightedBidCpm"
								label="Bid CPM"
								hint="0"
								placeholder="0"
								value={filters.weightedBidCpm}
								defaultValue="0"
								onChange={(field, value) =>
									onUpdateFilter("weightedBidCpm", value)
								}
							/>
						</div>
					</CategoryHeader>

					{/* Result Limit */}
					<div className="result-limit-section">
						<label className="result-limit-label">Result Limit:</label>
						<input
							type="number"
							value={filters?.noOfResult}
							onChange={(e) =>
								onUpdateFilter("noOfResult", parseInt(e.target.value)) || 250
							}
							placeholder="25"
							className="result-limit-input"
							min="1"
						/>
					</div>
				</div>
			</div>

			<style jsx>{`
                /* Base Styles */
                .filter-section-spacing > * {
                    margin-bottom: 16px;
                }

                .filter-section-spacing > *:last-child {
                    margin-bottom: 0;
                }

                /* Category Content Spacing Fix */
                .category-content {
                    padding: 16px 12px;
                    margin-top: 8px;
                    border-top: 1px solid #e5e7eb;
                }

                /* Demographics Always Visible Section */
                .demographics-always-visible {
                    margin-bottom: 20px;
                    padding: 16px;
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    border-left: 4px solid #0076BD;
                }

                .demographics-header {
                    margin-bottom: 12px;
                }

                .demographics-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #0076BD;
                    margin: 0;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #e2e8f0;
                }

                .demographics-content {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    margin-top: 12px;
                }

                /* Age Range Filter Styles */
                .age-range-container {
                    margin-bottom: 16px;
                }

                .age-range-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 8px;
                }

                .age-range-inputs {
                    display: flex;
                    gap: 12px;
                }

                .age-input-group {
                    flex: 1;
                }

                .age-input-label {
                    display: block;
                    font-size: 10px;
                    font-weight: 500;
                    color: #6b7280;
                    margin-bottom: 4px;
                }

                .age-input {
                    width: 100%;
                    padding: 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    font-size: 11px;
                    background: white;
                }

                .age-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 1px #0076BD;
                }

                /* Gender Filter Styles */
                .gender-filter-container {
                    margin-bottom: 16px;
                }

                .gender-filter-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 6px;
                }

                /* Location Filter Styles */
.location-filter-container {
    margin-bottom: 16px;
}

.location-filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.location-filter-label .selected-count {
    color: #0076BD;
    font-weight: 500;
    margin-left: 6px;
}

/* Location Type Selector */
.location-type-selector {
    margin-bottom: 12px;
}

.location-type-selector .tab-selector {
    display: flex;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
}

.location-type-selector .tab-button {
    flex: 1;
    padding: 8px 12px;
    font-size: 11px;
    border: none;
    background: transparent;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s ease;
}

.location-type-selector .tab-button.active {
    background: #0076BD;
    color: white;
}

.location-type-selector .tab-button:hover:not(.active) {
    background: #f3f4f6;
}

/* Selection Tags Display */
.location-selection-summary {
    margin-top: 8px;
    padding: 6px 8px;
    background: #f0f8ff;
    border: 1px solid #d1e7ff;
    border-radius: 4px;
}

.selection-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.selection-tag {
    background: #e6f3ff;
    color: #0076BD;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
    border: 1px solid #b3d9ff;
}

.selection-tag-more {
    background: #f3f4f6;
    color: #6b7280;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
    border: 1px solid #d1d5db;
}

                /* Multiselect Dropdown Styles */
                .multiselect-container {
                    position: relative;
                    margin-bottom: 12px;
                }

                .multiselect-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 6px;
                }

                .selected-count {
                    color: #0076BD;
                    font-weight: 500;
                    margin-left: 6px;
                }

                .multiselect-trigger {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    background: white;
                    cursor: pointer;
                    transition: border-color 0.2s ease;
                    font-size: 12px;
                }

                .multiselect-trigger:hover {
                    border-color: #0076BD;
                }

                .multiselect-trigger.open {
                    border-color: #0076BD;
                    box-shadow: 0 0 0 1px #0076BD;
                }

                .multiselect-placeholder {
                    color: #6b7280;
                    font-size: 11px;
                }

                /* Multiselect Dropdown Portal Styles */
                .multiselect-dropdown-portal {
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    max-height: 300px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .multiselect-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-top: none;
                    border-radius: 0 0 6px 6px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    z-index: 1000;
                    max-height: 300px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .multiselect-header {
                    padding: 8px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .multiselect-search {
                    width: 100%;
                    padding: 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    font-size: 11px;
                    margin-bottom: 6px;
                }

                .multiselect-search:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 1px #0076BD;
                }

                .multiselect-actions {
                    display: flex;
                    gap: 6px;
                }

                .select-all-btn, .clear-all-btn {
                    padding: 3px 6px;
                    border: 1px solid #d1d5db;
                    border-radius: 3px;
                    background: white;
                    color: #374151;
                    font-size: 10px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .select-all-btn:hover, .clear-all-btn:hover {
                    background: #f3f4f6;
                    border-color: #9ca3af;
                }

                .multiselect-options {
                    max-height: 200px;
                    overflow-y: auto;
                    padding: 4px;
                }

                .multiselect-option {
                    display: flex;
                    align-items: center;
                    padding: 6px 8px;
                    cursor: pointer;
                    border-radius: 3px;
                    transition: background-color 0.2s ease;
                    font-size: 11px;
                    gap: 8px;
                }

                .multiselect-option:hover {
                    background: #f3f4f6;
                }

                .multiselect-option.selected {
                    background: #eff6ff;
                    color: #0076BD;
                }

                .multiselect-option input[type="checkbox"] {
                    margin: 0;
                    accent-color: #0076BD;
                }

                .option-text {
                    flex: 1;
                    line-height: 1.3;
                }

                .no-options {
                    padding: 12px 8px;
                    text-align: center;
                    color: #6b7280;
                    font-size: 11px;
                    font-style: italic;
                }

                /* Tag Filter Styles */
                .tag-filter-container {
                    margin-bottom: 12px;
                }

                .tag-filter-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 6px;
                }

                .tag-filter-options {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                }

                .tag {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    border: 1px solid transparent;
                    user-select: none;
                }

                .tag-gray {
                    background: #f3f4f6;
                    color: #6b7280;
                    border-color: #e5e7eb;
                }

                .tag-gray:hover {
                    background: #e5e7eb;
                    color: #374151;
                    border-color: #9ca3af;
                }

                .tag-selected {
                    background: #0076BD !important;
                    color: white !important;
                    border-color: #0056b3 !important;
                    box-shadow: 0 1px 3px rgba(0, 118, 189, 0.3);
                    transform: translateY(-1px);
                }

                .tag-selected:hover {
                    background: #0056b3 !important;
                    color: white !important;
                    border-color: #004494 !important;
                    transform: translateY(-1px);
                }

                .tag-clickable {
                    user-select: none;
                }

                /* Boolean Filter Styles */
                .boolean-filter-container {
                    margin-bottom: 12px;
                }

                .boolean-filter-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 6px;
                }

                .tab-selector {
                    display: flex;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    overflow: hidden;
                }

                .tab-button {
                    flex: 1;
                    padding: 6px 8px;
                    font-size: 10px;
                    border: none;
                    background: transparent;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .tab-button.active {
                    background: #0076BD;
                    color: white;
                }

                .tab-button:hover:not(.active) {
                    background: #f3f4f6;
                }

                /* Threshold Filter Styles */
.threshold-filter-container {
    margin-bottom: 16px;
}

.threshold-filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
}

.threshold-filter-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.threshold-input-group {
    display: flex;
    flex-direction: column;
}

.threshold-input-label {
    font-size: 10px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.threshold-hint {
    font-size: 9px;
    color: #6b7280;
    font-weight: 400;
    background: #f3f4f6;
    padding: 1px 4px;
    border-radius: 3px;
}

.threshold-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.threshold-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 11px;
    background: white;
    transition: border-color 0.2s ease;
}

.threshold-input:focus {
    outline: none;
    border-color: #0076BD;
    box-shadow: 0 0 0 1px #0076BD;
}

.threshold-display {
    font-size: 9px;
    color: #0076BD;
    font-weight: 500;
    background: #eff6ff;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1px solid #bfdbfe;
}

.threshold-help {
    margin-top: 8px;
    padding: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}

.threshold-help small {
    font-size: 10px;
    color: #6b7280;
    line-height: 1.3;
}

/* Category Set Filter Styles */
.category-set-filter-container {
    margin-bottom: 16px;
}

.category-set-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.category-set-label {
    font-size: 11px;
    font-weight: 600;
    color: #374151;
}

.add-category-btn {
    padding: 4px 8px;
    font-size: 10px;
    background: #0076BD;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.add-category-btn:hover {
    background: #0056b3;
}

.category-filters-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.category-filter-item {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #f9fafb;
    overflow: hidden;
}

.category-filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
}

.filter-number {
    font-size: 10px;
    font-weight: 600;
    color: #374151;
}

.remove-filter-btn {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    padding: 2px;
    border-radius: 2px;
    transition: background-color 0.2s ease;
}

.remove-filter-btn:hover {
    background: #fee2e2;
}

.category-filter-content {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.filter-field-label {
    font-size: 10px;
    font-weight: 500;
    color: #374151;
}

.category-select {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 11px;
    background: white;
    color: #374151;
}

.category-select:focus {
    outline: none;
    border-color: #0076BD;
    box-shadow: 0 0 0 1px #0076BD;
}

.min-max-inputs {
    display: flex;
    gap: 8px;
}

.min-max-inputs .filter-field {
    flex: 1;
}

.category-filter-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 11px;
    background: white;
    transition: border-color 0.2s ease;
}

.category-filter-input:focus {
    outline: none;
    border-color: #0076BD;
    box-shadow: 0 0 0 1px #0076BD;
}

.category-filter-input.disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
    border-color: #e5e7eb;
}

.category-filter-input.disabled:focus {
    border-color: #e5e7eb;
    box-shadow: none;
}

.attribute-display {
    margin-top: 6px;
    padding: 6px 8px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 4px;
}

.attribute-label {
    font-size: 10px;
    color: #0076BD;
    font-weight: 500;
}

.no-filters-message {
    padding: 16px;
    text-align: center;
    color: #6b7280;
    font-size: 11px;
    font-style: italic;
    background: #f9fafb;
    border: 1px dashed #d1d5db;
    border-radius: 6px;
}

                /* Error Message */
                .error-message {
                    padding: 12px;
                    margin-bottom: 16px;
                    background: #fee2e2;
                    border: 1px solid #fecaca;
                    border-radius: 6px;
                    color: #991b1b;
                }

                .error-message p {
                    font-size: 12px;
                    margin: 0 0 8px 0;
                }

                .retry-btn {
                    font-size: 11px;
                    color: #991b1b;
                    background: none;
                    border: none;
                    text-decoration: underline;
                    cursor: pointer;
                }

                /* Result Limit */
                .result-limit-section {
                    margin-top: 16px;
                    padding: 12px;
                    border-top: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .result-limit-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 4px;
                }

                .result-limit-input {
                    width: 100%;
                    padding: 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    font-size: 11px;
                    background: white;
                }

                .result-limit-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 1px #0076BD;
                }

                /* Scrollbar Styling */
                .multiselect-options::-webkit-scrollbar {
                    width: 6px;
                }

                .multiselect-options::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                .multiselect-options::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }

                .multiselect-options::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
            `}</style>
		</>
	);
};
